import React, { useState, useRef, useEffect, useMemo } from 'react';
import CalendarComponent from '../../../../../Calendar/CalendarComponent';
import './AddTransactionHistory.css';
import { axiosInstance } from '../../../../../../utils/tokenUtils';
import { toast } from 'react-hot-toast';

const StatusColor = {
  // Pending: { color: '#E0AD14', bgcolor: '#FFFCF2', value: 'Pending' },
  Failed: { color: '#E14949', bgcolor: '#FFF2F2', value: 'Failed' },
  Success: { color: '#14B334', bgcolor: '#F2FAF3', value: 'Success' },
};

const AddTransactionHistory = ({
  open = false,
  onClose,
  onSave,
  transaction,
  requestedPayout = 0,
  payoutRequestId,
}) => {
  const [form, setForm] = useState({
    transactionId: '',
    date: '',
    bankDeduction: '',
    actualAmount: '',
    status: '',
    payoutAmountRequested: 100,
    failedReason: '',
  });

  const [statusOpen, setStatusOpen] = useState(false);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const dropdownRef = useRef(null);

  const maxSelectableDate = useMemo(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), today.getDate());
  }, [open]);

  useEffect(() => {
    const handleClickOutside = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setStatusOpen(false);
      }
    };
    if (statusOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [statusOpen]);

  useEffect(() => {
    if (transaction) {
      setForm({
        transactionId: transaction.id?.toString() || '',
        date: transaction.date || '',
        bankDeduction: transaction.bank?.toString() || '',
        actualAmount: transaction.actual?.toString() || '',
        status: transaction.status || '',
        payoutAmountRequested: transaction.payoutAmountRequested || requestedPayout,
        failedReason: transaction.failed_reason || '',
      });
    } else {
      setForm({
        transactionId: '',
        date: '',
        bankDeduction: '',
        actualAmount: '',
        status: '',
        payoutAmountRequested: requestedPayout,
        failedReason: '',
      });
    }
  }, [transaction, requestedPayout]);

  useEffect(() => {
    const fetchPayout = async () => {
      if (!open || !payoutRequestId) return;
      try {
        const { data } = await axiosInstance.get(
          `/admin/transactions/payout-request/${payoutRequestId}`
        );
        const toDisplayDate = iso => {
          if (!iso) return '';
          try {
            const d = new Date(iso);
            const dd = String(d.getDate()).padStart(2, '0');
            const mm = String(d.getMonth() + 1).padStart(2, '0');
            const yyyy = d.getFullYear();
            return `${dd}/${mm}/${yyyy}`;
          } catch {
            return '';
          }
        };
        setForm(prev => ({
          ...prev,
          payoutAmountRequested:
            typeof data?.total_amount === 'number' ? data.total_amount : prev.payoutAmountRequested,
          transactionId: data?.transaction_id ? String(data.transaction_id) : '',
          date: data?.transaction_date ? toDisplayDate(data.transaction_date) : '',
          bankDeduction:
            data?.bank_deduction === null || data?.bank_deduction === undefined
              ? ''
              : String(data.bank_deduction),
          actualAmount:
            data?.actual_amount === null || data?.actual_amount === undefined
              ? ''
              : String(data.actual_amount),
          status:
            data?.status?.toLowerCase() === 'success'
              ? 'Success'
              : data?.status?.toLowerCase() === 'failed'
                ? 'Failed'
                : prev.status,
          failedReason: data?.failed_reason || prev.failedReason,
        }));
      } catch (_) {
        // ignore prefill errors
      }
    };
    fetchPayout();
  }, [open, payoutRequestId]);

  useEffect(() => {
    const requested = parseFloat(form.payoutAmountRequested) || 0;
    const deduction = parseFloat(form.bankDeduction) || 0;
    const actual = requested - deduction;
    setForm(prev => ({ ...prev, actualAmount: actual.toFixed(2) }));
  }, [form.bankDeduction, form.payoutAmountRequested]);

  const handleChange = (key, value) => {
    if (key === 'bankDeduction') {
      if (value === '' || /^\d*\.?\d*$/.test(value)) {
        const requested = parseFloat(form.payoutAmountRequested) || 0;
        const deduction = parseFloat(value) || 0;

        if (deduction <= requested) {
          setForm(prev => ({ ...prev, [key]: value }));
        }
      }
    } else {
      setForm(prev => ({ ...prev, [key]: value }));
    }
  };

  const handleDateSelect = date => {
    const selected = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    if (selected > maxSelectableDate) {
      toast.error('Transaction date cannot be in the future');
      return;
    }
    const formattedDate = date.toLocaleDateString('en-GB');
    setForm(prev => ({ ...prev, date: formattedDate }));
    setCalendarOpen(false);
  };

  const formatCurrency = value => {
    if (!value) return '';
    return parseFloat(value).toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const handleSave = () => {
    const toIsoDate = d => {
      if (!d) return '';
      try {
        const [dd, mm, yyyy] = d.split('/');
        return `${yyyy}-${mm.padStart(2, '0')}-${dd.padStart(2, '0')}`;
      } catch (_) {
        return d;
      }
    };

    const numeric = v => {
      if (v === '' || v === null || v === undefined) return 0;
      const s = String(v).replace(/,/g, '');
      const n = parseFloat(s);
      return isNaN(n) ? 0 : n;
    };

    const payloadCommon = {
      transaction_id: form.transactionId,
      transaction_date: toIsoDate(form.date),
      bank_deduction: numeric(form.bankDeduction),
      actual_amount: numeric(form.actualAmount),
    };

    const isEdit = !!transaction;

    const run = async () => {
      try {
        if (!payoutRequestId) {
          toast.error('Missing payout request id');
          return;
        }

        if (isEdit) {
          await axiosInstance.patch(`/admin/transactions/edit-payout-request/${payoutRequestId}`, {
            ...payloadCommon,
            ...(String(form.status).toLowerCase() === 'failed' && {
              failed_reason: form.failedReason?.trim() || null,
            }),
          });
          toast.success('Payout updated');
        } else {
          const body = {
            ...payloadCommon,
            total_amount: numeric(form.payoutAmountRequested),
            status: (form.status || '').toLowerCase(),
          };
          if ((form.status || '').toLowerCase() === 'failed' && form.failedReason?.trim()) {
            body.failed_reason = form.failedReason.trim();
          }
          await axiosInstance.patch(`/admin/transactions/payout-request/${payoutRequestId}`, body);
          toast.success('Payout added');
        }

        if (onSave) onSave(form);
        if (onClose) onClose();
      } catch (error) {
        toast.error(error?.response?.data?.detail || 'Operation failed');
      }
    };

    run();
  };

  const validateForm = () => {
    const {
      transactionId,
      date,
      bankDeduction,
      actualAmount,
      payoutAmountRequested,
      status,
      failedReason,
    } = form;

    if (!transactionId || !date || !bankDeduction || !actualAmount || !payoutAmountRequested) {
      return false;
    }

    try {
      const [dd, mm, yyyy] = date.split('/');
      const selected = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      if (selected > maxSelectableDate) {
        return false;
      }
    } catch {
      return false;
    }

    const bankDeductionAmount = parseFloat(bankDeduction) || 0;
    const actualAmountValue = parseFloat(actualAmount) || 0;
    const requestedAmount = parseFloat(payoutAmountRequested) || 0;

    if (actualAmountValue !== requestedAmount - bankDeductionAmount) {
      return false;
    }

    if (actualAmountValue < 0) {
      return false;
    }

    if (!transaction) {
      if (!status) return false;
      if ((status || '').toLowerCase() === 'failed' && !failedReason?.trim()) return false;
    }

    return true;
  };

  const isFormValid = validateForm();

  if (!open) return null;

  return (
    <div className="payoutth-overlay">
      <div className="payoutth-modal">
        <div className="payoutth-header">
          <div className="payoutth-title">
            {transaction ? 'Edit Transaction History' : 'Add Transaction History'}
          </div>
          <button
            type="button"
            className="payoutth-close-btn"
            onClick={onClose}
            aria-label="Close"
          />
        </div>

        <div className="payoutth-requested-payout">
          Requested Payout:{' '}
          <span className="payoutth-amount">{form.payoutAmountRequested.toFixed(2)} Riyal</span>
        </div>

        <div className="payoutth-table">
          <table className="payoutth-table-container">
            <thead>
              <tr>
                <th className="payoutth-th">Transaction ID</th>
                <th className="payoutth-th">Transaction Date</th>
                <th className="payoutth-th">Payout Amount Requested</th>
                <th className="payoutth-th">Bank Deduction</th>
                <th className="payoutth-th">Actual Amount</th>
                <th className="payoutth-th">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="payoutth-td">
                  <input
                    className="payoutth-input"
                    placeholder="XXXX"
                    value={form.transactionId}
                    onChange={e => handleChange('transactionId', e.target.value)}
                  />
                </td>
                <td className="payoutth-td">
                  <div className="payoutth-date-container">
                    <input
                      className="payoutth-input"
                      placeholder="DD/MM/YYYY"
                      value={form.date}
                      onClick={() => setCalendarOpen(true)}
                      readOnly
                    />
                    {calendarOpen && (
                      <CalendarComponent
                        isOpen={calendarOpen}
                        onClose={() => setCalendarOpen(false)}
                        onDateSelect={handleDateSelect}
                        maxDate={maxSelectableDate}
                        selectedDate={
                          form.date ? new Date(form.date.split('/').reverse().join('-')) : null
                        }
                      />
                    )}
                  </div>
                </td>
                <td className="payoutth-td">
                  <input
                    className="payoutth-input"
                    placeholder="XXXX"
                    value={form.payoutAmountRequested.toFixed(2)}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </td>
                <td className="payoutth-td">
                  <input
                    className="payoutth-input"
                    placeholder="XXXX"
                    value={form.bankDeduction}
                    onChange={e => handleChange('bankDeduction', e.target.value)}
                    onBlur={e => handleChange('bankDeduction', formatCurrency(e.target.value))}
                    style={{
                      borderColor:
                        parseFloat(form.bankDeduction) > parseFloat(form.payoutAmountRequested)
                          ? '#E14949'
                          : undefined,
                    }}
                  />
                  {parseFloat(form.bankDeduction) > parseFloat(form.payoutAmountRequested) && (
                    <div style={{ color: '#E14949', fontSize: '12px', marginTop: '4px' }}>
                      Cannot exceed requested amount
                    </div>
                  )}
                </td>
                <td className="payoutth-td">
                  <input
                    className="payoutth-input"
                    placeholder="XXXX"
                    value={form.actualAmount}
                    readOnly
                    style={{ backgroundColor: '#f8f9fa', cursor: 'not-allowed' }}
                  />
                </td>
                <td className="payoutth-td">
                  <div className="payoutth-select" ref={dropdownRef}>
                    <button
                      type="button"
                      className="payoutth-select-trigger"
                      onClick={() => !transaction && setStatusOpen(o => !o)}
                      disabled={!!transaction}
                      style={{
                        opacity: transaction ? 0.6 : 1,
                        cursor: transaction ? 'not-allowed' : 'pointer',
                        color:
                          form.status && StatusColor[form.status]
                            ? StatusColor[form.status].color
                            : '#14171f',
                      }}
                    >
                      {form.status || 'Select'}
                      <span className="payoutth-caret" />
                    </button>
                    {statusOpen && !transaction && (
                      <div className="payoutth-select-menu">
                        {Object.entries(StatusColor).map(([key, config]) => (
                          <div
                            key={key}
                            className="payoutth-option"
                            style={{ color: config.color }}
                            onClick={() => {
                              handleChange('status', config.value);
                              setStatusOpen(false);
                            }}
                          >
                            {config.value}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
              {(form.status || '').toLowerCase() === 'failed' && (
                <tr>
                  <td className="payoutth-td" colSpan={6}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                      <span style={{ fontFamily: 'Poppins', fontSize: '12px', color: '#14171f' }}>
                        Failure reason
                      </span>
                      <input
                        className="payoutth-input payoutth-failed-reason"
                        placeholder="Enter failure reason"
                        value={form.failedReason}
                        onChange={e => handleChange('failedReason', e.target.value)}
                      />
                    </div>
                    {!transaction && !form.failedReason?.trim() && (
                      <div className="payoutth-validation-error" style={{ marginTop: '6px' }}>
                        Failure reason is required when status is Failed
                      </div>
                    )}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="payoutth-footer">
          <div className="payoutth-footer-buttons">
            <button type="button" className="payoutth-btn payoutth-btn-outline" onClick={onClose}>
              Cancel
            </button>
            <button
              type="button"
              className="payoutth-btn payoutth-btn-primary"
              onClick={handleSave}
              disabled={!isFormValid}
              style={{
                opacity: isFormValid ? 1 : 0.6,
                cursor: isFormValid ? 'pointer' : 'not-allowed',
              }}
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddTransactionHistory;
